/**
 * useFormValidation Hook
 * Standardized form validation logic with real-time feedback
 * 
 * Features:
 * - Form state management
 * - Field-level validation
 * - Real-time error clearing
 * - Submit validation
 * - Dirty state tracking
 * 
 * Usage:
 * const { formData, errors, isDirty, isValid, setFieldValue, handleSubmit } = useFormValidation({
 *   initialValues: { name: '', email: '' },
 *   validationSchema: (values) => ({
 *     name: !values.name ? 'Name is required' : '',
 *     email: !values.email ? 'Email is required' : ''
 *   }),
 *   onSubmit: async (values) => { ... }
 * });
 */

import { useState, useCallback, useRef } from 'react';

const useFormValidation = ({
  initialValues = {},
  validationSchema = () => ({}),
  onSubmit = async () => {},
  validateOnChange = true,
  validateOnBlur = true,
  validateOnSubmit = true
} = {}) => {
  const [formData, setFormData] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const initialValuesRef = useRef(initialValues);

  // Validate entire form
  const validateForm = useCallback((values = formData) => {
    const newErrors = validationSchema(values);
    setErrors(newErrors);
    return Object.keys(newErrors).every(key => !newErrors[key]);
  }, [formData, validationSchema]);

  // Validate single field
  const validateField = useCallback((fieldName, value = formData[fieldName]) => {
    const newErrors = validationSchema({
      ...formData,
      [fieldName]: value
    });

    setErrors(prev => ({
      ...prev,
      [fieldName]: newErrors[fieldName] || ''
    }));

    return !newErrors[fieldName];
  }, [formData, validationSchema]);

  // Set field value
  const setFieldValue = useCallback((fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }

    // Validate on change if enabled
    if (validateOnChange && touched[fieldName]) {
      validateField(fieldName, value);
    }
  }, [errors, touched, validateOnChange, validateField]);

  // Set field error
  const setFieldError = useCallback((fieldName, error) => {
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  }, []);

  // Mark field as touched
  const setFieldTouched = useCallback((fieldName, isTouched = true) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: isTouched
    }));

    // Validate on blur if enabled
    if (validateOnBlur && isTouched) {
      validateField(fieldName);
    }
  }, [validateOnBlur, validateField]);

  // Handle input change
  const handleFieldChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setFieldValue(name, fieldValue);
  }, [setFieldValue]);

  // Handle field blur
  const handleFieldBlur = useCallback((e) => {
    const { name } = e.target;
    setFieldTouched(name, true);
  }, [setFieldTouched]);

  // Reset form
  const resetForm = useCallback((newInitialValues = initialValuesRef.current) => {
    setFormData(newInitialValues);
    setErrors({});
    setTouched({});
    setSubmitError(null);
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    // Validate on submit if enabled
    if (validateOnSubmit && !validateForm()) {
      // Mark all fields as touched
      const allTouched = Object.keys(formData).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setTouched(allTouched);
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      const result = await onSubmit(formData);

      // Handle errors returned from onSubmit (e.g., { general: 'error', fieldName: 'error' })
      if (result && typeof result === 'object') {
        const hasErrors = Object.values(result).some(val => val);
        if (hasErrors) {
          setErrors(prev => ({ ...prev, ...result }));
          setSubmitError(result.general || 'Validation failed');
          return;
        }
      }
    } catch (error) {
      setSubmitError(error.message || 'An error occurred while submitting the form');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, validateOnSubmit, onSubmit]);

  // Calculate dirty state
  const isDirty = Object.keys(initialValuesRef.current).some(
    key => formData[key] !== initialValuesRef.current[key]
  );

  // Calculate valid state
  const isValid = Object.keys(errors).every(key => !errors[key]) && isDirty;

  return {
    // State
    formData,
    errors,
    touched,
    isDirty,
    isValid,
    isSubmitting,
    submitError,

    // Setters
    setFieldValue,
    setFieldError,
    setFieldTouched,
    setFormData,
    setErrors,

    // Handlers
    handleFieldChange,
    handleFieldBlur,
    handleSubmit,
    resetForm,

    // Validators
    validateForm,
    validateField
  };
};

export { useFormValidation };
